import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowRight, Check, Film, Clock, Video, Monitor, Smartphone, Square, 
  User, MapPin, Star, ChevronDown, ChevronUp, Layers, HardDrive, Clapperboard,
  Plus, Trash2, Copy, Info, MessageSquare, FileText
} from 'lucide-react';
import Navbar from '../components/Navbar';
import { toast } from '../components/ui/sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://revocut-production.up.railway.app';

// Info Tooltip Component
const InfoTooltip = ({ text }) => {
  const [show, setShow] = useState(false);
  
  if (!text) return null;
  
  return (
    <div className="relative inline-block ml-1">
      <button
        type="button"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onClick={(e) => { e.stopPropagation(); setShow(!show); }}
        className="w-4 h-4 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
      >
        <Info className="w-3 h-3 text-gray-400" />
      </button>
      {show && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 text-xs text-white bg-gray-800 rounded-lg shadow-lg whitespace-nowrap max-w-xs">
          <div className="whitespace-normal">{text}</div>
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
        </div>
      )}
    </div>
  );
};

const Calculator = () => {
  const navigate = useNavigate();
  const { teamId } = useParams();
  
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pricingConfig, setPricingConfig] = useState(null);
  const [freelancerPricing, setFreelancerPricing] = useState(null);
  const [marginTiers, setMarginTiers] = useState([]);
  
  // Multi-video state - Array of video configs
  const [videos, setVideos] = useState([]);
  const [activeVideoIndex, setActiveVideoIndex] = useState(0);
  const [showSummary, setShowSummary] = useState(false);
  
  // Expanded addon categories
  const [expandedCategories, setExpandedCategories] = useState({});

  // Helper: Create default video config
  const createDefaultVideo = (config) => ({
    id: Date.now(),
    format: config?.video_formats?.[0]?.id || '16_9',
    videoType: config?.video_types?.[0]?.id || 'vlog',
    language: 'de', // Sprache
    subtitleType: 'none', // Untertitel-Typ
    subtitleColor: '#FFFFFF', // Untertitel-Farbe
    videoCount: 1,
    minutesPerVideo: 3,
    rawMaterialMinutes: 30,
    needsBRoll: false,
    rushDelivery: 'none', // Rush Delivery Option
    extraRevisions: 'none', // Extra Revisionen Option
    notes: '', // Notizen pro Video
    addons: {}
  });

  // Dynamic rush delivery options
  const rushDeliveryOptions = (pricingConfig?.rush_delivery_options || [
    { id: 'none', name: 'Standard-Lieferung', percentage: 0 },
    { id: '48h', name: 'Rush Delivery 48h', percentage: 30 },
    { id: '24h', name: 'Rush Delivery 24h', percentage: 50 },
  ]);

  // Dynamic extra revision options
  const extraRevisionOptions = (pricingConfig?.extra_revision_options || [
    { id: 'none', name: 'Keine Extra-Revisionen', flat_price: 0 },
    { id: '1', name: '1 Extra Revision', flat_price: 25 },
    { id: '3', name: '3 Extra Revisionen', flat_price: 60 },
  ]).map(er => ({
    id: er.id,
    name: er.name,
    price: er.flat_price || 0,
  }));

  // Kategorien die NUR vom Admin/Plattform kontrolliert werden (nicht von Freelancern)
  const platformOnlyCategories = ['upload_publishing'];
  
  // Kategorien die im Calculator als separate Optionen angezeigt werden (nicht in der Addon-Liste)
  const separateOptionCategories = ['workflow', 'broll_assets'];

  // Dynamic from pricing config (with fallbacks)
  const languages = pricingConfig?.languages || [
    { id: 'de', name: 'Deutsch' },
    { id: 'en', name: 'Englisch' },
    { id: 'other', name: 'Andere' },
  ];

  // Dynamic subtitle types
  const subtitleTypes = (pricingConfig?.subtitle_types || [
    { id: 'none', name: 'Keine Untertitel', flat_price: 0, has_color: false },
    { id: 'standard', name: 'Standard Untertitel', flat_price: 10, has_color: true },
    { id: 'animated', name: 'Animierte Untertitel', flat_price: 20, has_color: true },
    { id: 'karaoke', name: 'Karaoke-Style', flat_price: 25, has_color: true },
  ]).map(st => ({
    id: st.id,
    name: st.name,
    price: st.flat_price || 0,
    hasColor: st.has_color !== false && st.id !== 'none',
  }));

  // Dynamic subtitle colors
  const subtitleColors = pricingConfig?.subtitle_colors || [
    { id: '#FFFFFF', name: 'Weiß' },
    { id: '#FFFF00', name: 'Gelb' },
    { id: '#00FF00', name: 'Grün' },
    { id: '#00FFFF', name: 'Cyan' },
    { id: '#FF6B6B', name: 'Rot' },
  ];

  // Default pricing config fallback
  const defaultPricingConfig = {
    video_formats: [
      { id: '16_9', name: '16:9 Landscape', description: 'YouTube, Vimeo', icon: 'monitor' },
      { id: '9_16', name: '9:16 Portrait', description: 'TikTok, Reels, Shorts', icon: 'smartphone' },
      { id: '1_1', name: '1:1 Square', description: 'Instagram Feed', icon: 'square' },
      { id: '4_5', name: '4:5 Portrait', description: 'Instagram Feed optimal', icon: 'smartphone' },
      { id: '21_9', name: '21:9 Cinematic', description: 'Filmisches Breitbild', icon: 'film' },
    ],
    video_types: [
      { id: 'vlog', name: 'Vlog', description: 'Standard YouTube Vlog' },
      { id: 'talking_head', name: 'Talking Head', description: 'Erklärvideo, Interview' },
      { id: 'short_form', name: 'Short Form', description: 'TikTok, Reels, Shorts' },
      { id: 'car_edit', name: 'Car Edit', description: 'Dynamische Auto-Videos' },
      { id: 'commercial', name: 'Werbeclip', description: 'Commercial, Werbespot' },
    ],
    addon_categories: [
      {
        id: 'motion_graphics',
        name: 'Motion & Grafiken',
        addons: [
          { id: 'motion_design', name: 'Motion Design', percentage: 30.0 },
          { id: 'color_grading', name: 'Color Grading', percentage: 15.0 },
          { id: 'thumbnails', name: 'Thumbnails', percentage: null, flat_price: 25.0 },
        ]
      },
      {
        id: 'extras',
        name: 'Extras',
        addons: [
          { id: 'rush_48h', name: 'Rush Delivery 48h', percentage: 30.0 },
          { id: 'rush_24h', name: 'Rush Delivery 24h', percentage: 50.0 },
          { id: 'extra_revision', name: '1 Extra Revision', percentage: null, flat_price: 25.0 },
        ]
      }
    ],
    default_base_price: 80
  };

  // Rohmaterial Faktor berechnen
  const getRawMaterialFactor = (rawMaterialMinutes) => {
    if (rawMaterialMinutes <= 5) return 0.7;
    if (rawMaterialMinutes <= 15) return 0.85;
    if (rawMaterialMinutes <= 30) return 1.0;
    if (rawMaterialMinutes <= 60) return 1.15;
    if (rawMaterialMinutes <= 120) return 1.3;
    if (rawMaterialMinutes <= 300) return 1.5;
    if (rawMaterialMinutes <= 600) return 1.8;
    return 2.0;
  };
  
  // B-Roll Faktor
  const getBRollFactor = (needsBRoll) => needsBRoll ? 1.15 : 1.0;

  // Format raw material time
  const formatRawMaterial = (minutes) => {
    if (minutes < 1) {
      return `${Math.round(minutes * 60)} Sek`;
    } else if (minutes < 60) {
      return `${Math.round(minutes)} Min`;
    } else {
      const hours = Math.floor(minutes / 60);
      const mins = Math.round(minutes % 60);
      return mins > 0 ? `${hours} Std ${mins} Min` : `${hours} Std`;
    }
  };

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        let config = null;
        try {
          const configRes = await fetch(`${API_URL}/api/pricing/config`);
          if (configRes.ok) {
            const contentType = configRes.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              config = await configRes.json();
            }
          }
        } catch (configError) {
          console.warn('Could not fetch pricing config, using defaults:', configError);
        }
        
        try {
          const marginRes = await fetch(`${API_URL}/api/platform-settings`);
          if (marginRes.ok) {
            const marginData = await marginRes.json();
            setMarginTiers(marginData.margin_tiers || []);
          }
        } catch (marginError) {
          console.warn('Could not fetch margin settings:', marginError);
        }
        
        const finalConfig = config || defaultPricingConfig;
        setPricingConfig(finalConfig);
        
        // Initialize first video
        setVideos([createDefaultVideo(finalConfig)]);
        
        if (teamId) {
          const teamRes = await fetch(`${API_URL}/api/teams/${teamId}`);
          if (teamRes.ok) {
            const contentType = teamRes.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              const teamData = await teamRes.json();
              setTeam(teamData);
              
              const freelancerId = teamData.freelancer_user_id || teamData.freelancer_id;
              if (freelancerId) {
                try {
                  const pricingRes = await fetch(`${API_URL}/api/pricing/freelancer/${freelancerId}`);
                  if (pricingRes.ok) {
                    const pricingContentType = pricingRes.headers.get('content-type');
                    if (pricingContentType && pricingContentType.includes('application/json')) {
                      const pricingData = await pricingRes.json();
                      setFreelancerPricing(pricingData);
                    }
                  }
                } catch (pricingError) {
                  console.warn('Could not fetch freelancer pricing:', pricingError);
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setPricingConfig(defaultPricingConfig);
        setVideos([createDefaultVideo(defaultPricingConfig)]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [teamId]);

  const basePrice = useMemo(() => {
    if (freelancerPricing?.base_price_per_minute) {
      return freelancerPricing.base_price_per_minute;
    }
    return pricingConfig?.default_base_price || 80;
  }, [freelancerPricing, pricingConfig]);

  // Calculate subtotal for a single video
  const calculateVideoSubtotal = (video) => {
    const rawMaterialFactor = getRawMaterialFactor(video.rawMaterialMinutes);
    const bRollFactor = getBRollFactor(video.needsBRoll);
    
    const totalMinutes = video.videoCount * video.minutesPerVideo;
    const baseTotal = totalMinutes * basePrice * rawMaterialFactor * bRollFactor;
    
    // Untertitel-Kosten (use freelancer custom price if available)
    const subtitleType = subtitleTypes.find(s => s.id === video.subtitleType);
    let subtitlePrice = subtitleType?.price || 0;
    if (freelancerPricing?.pricing?.custom_subtitle_prices?.[video.subtitleType] !== undefined) {
      subtitlePrice = freelancerPricing.pricing.custom_subtitle_prices[video.subtitleType];
    }
    const subtitleCost = baseTotal * (subtitlePrice / 100);
    
    // Rush Delivery Kosten (use freelancer custom percentage if available)
    const rushOption = rushDeliveryOptions.find(r => r.id === video.rushDelivery);
    let rushPercentage = rushOption?.percentage || 0;
    if (freelancerPricing?.pricing?.custom_rush_percentages?.[video.rushDelivery] !== undefined) {
      rushPercentage = freelancerPricing.pricing.custom_rush_percentages[video.rushDelivery];
    }
    const rushDeliveryCost = baseTotal * (rushPercentage / 100);
    
    // Extra Revisionen Kosten (use freelancer custom price if available)
    const revisionOption = extraRevisionOptions.find(r => r.id === video.extraRevisions);
    let revisionPrice = revisionOption?.price || 0;
    if (freelancerPricing?.pricing?.custom_revision_prices?.[video.extraRevisions] !== undefined) {
      revisionPrice = freelancerPricing.pricing.custom_revision_prices[video.extraRevisions];
    }
    const extraRevisionsCost = revisionPrice * video.videoCount;
    
    let addonsCost = 0;
    if (pricingConfig?.addon_categories) {
      pricingConfig.addon_categories.forEach(category => {
        // Skip workflow (now handled above as separate dropdowns)
        if (separateOptionCategories.includes(category.id)) return;
        
        category.addons?.forEach(addon => {
          if (video.addons[addon.id]) {
            const customPrice = freelancerPricing?.pricing?.custom_prices?.[addon.id];
            const customPriceType = freelancerPricing?.pricing?.custom_price_types?.[addon.id];
            const isEnabled = freelancerPricing?.pricing?.enabled_addons?.[addon.id] !== false;
            
            if (freelancerPricing && !isEnabled) return;
            
            const priceType = customPriceType || (addon.percentage !== null ? 'percent' : 'euro');
            const priceValue = customPrice !== undefined ? customPrice : (addon.percentage || addon.flat_price || 0);
            
            if (priceType === 'percent') {
              addonsCost += baseTotal * (priceValue / 100);
            } else if (priceType === 'multiplier') {
              addonsCost += baseTotal * (priceValue - 1);
            } else {
              addonsCost += priceValue * video.videoCount;
            }
          }
        });
      });
    }
    
    return { 
      baseTotal, 
      addonsCost, 
      subtitleCost, 
      rushDeliveryCost, 
      extraRevisionsCost,
      subtotal: baseTotal + addonsCost + subtitleCost + rushDeliveryCost + extraRevisionsCost, 
      rawMaterialFactor, 
      bRollFactor 
    };
  };

  // Calculate totals for all videos
  const calculation = useMemo(() => {
    let totalNettoSum = 0;
    let totalVideoCount = 0;
    const videoCalcs = videos.map(video => {
      const calc = calculateVideoSubtotal(video);
      totalNettoSum += calc.subtotal;
      totalVideoCount += video.videoCount;
      return { ...video, ...calc };
    });
    
    let marginPercent = 20;
    if (marginTiers.length > 0) {
      const sortedTiers = [...marginTiers].sort((a, b) => b.min_amount - a.min_amount);
      for (const tier of sortedTiers) {
        if (totalNettoSum >= tier.min_amount) {
          marginPercent = tier.margin_percent;
          break;
        }
      }
    }
    
    const marginAmount = totalNettoSum * (marginPercent / 100);
    const total = totalNettoSum + marginAmount;
    
    return { videoCalcs, nettoTotal: totalNettoSum, marginPercent, marginAmount, total, totalVideoCount };
  }, [videos, basePrice, pricingConfig, freelancerPricing, marginTiers]);

  // Update a specific video
  const updateVideo = (index, updates) => {
    setVideos(prev => prev.map((v, i) => i === index ? { ...v, ...updates } : v));
  };

  // Toggle addon for active video
  const toggleAddon = (addonId) => {
    const video = videos[activeVideoIndex];
    updateVideo(activeVideoIndex, { addons: { ...video.addons, [addonId]: !video.addons[addonId] } });
  };

  // Add new video
  const addVideo = () => {
    setVideos(prev => [...prev, createDefaultVideo(pricingConfig)]);
    setActiveVideoIndex(videos.length);
    setShowSummary(false);
    toast.success('Video hinzugefügt');
  };

  // Remove video
  const removeVideo = (index) => {
    if (videos.length <= 1) return;
    setVideos(prev => prev.filter((_, i) => i !== index));
    if (activeVideoIndex >= videos.length - 1) {
      setActiveVideoIndex(Math.max(0, videos.length - 2));
    }
    toast.success('Video entfernt');
  };

  // Duplicate video
  const duplicateVideo = (index) => {
    const copy = { ...videos[index], id: Date.now() };
    const newVideos = [...videos];
    newVideos.splice(index + 1, 0, copy);
    setVideos(newVideos);
    setActiveVideoIndex(index + 1);
    toast.success('Video dupliziert');
  };

  const handleContinue = () => {
    sessionStorage.setItem('revocut_calculation', JSON.stringify({
      team, teamId, videos, calculation, basePrice
    }));
    navigate('/request');
  };

  const availableFormats = useMemo(() => {
    if (!pricingConfig?.video_formats) return [];
    if (freelancerPricing?.pricing?.enabled_formats) {
      return pricingConfig.video_formats.filter(f => 
        freelancerPricing.pricing.enabled_formats.includes(f.id)
      );
    }
    return pricingConfig.video_formats;
  }, [pricingConfig, freelancerPricing]);

  const getAvailableAddons = (category) => {
    if (!category.addons) return [];
    if (freelancerPricing?.pricing?.enabled_addons) {
      return category.addons.filter(a => 
        freelancerPricing.pricing.enabled_addons[a.id] !== false
      );
    }
    return category.addons;
  };

  // Get active video
  const activeVideo = videos[activeVideoIndex] || {};

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{ borderColor: '#5C63FF', borderTopColor: 'transparent' }} />
          <p className="text-gray-400">Wird geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: '#0a0a0a' }}>
      <Navbar />

      <main className="px-6 lg:px-12 pt-24 pb-32">
        <div className="max-w-5xl mx-auto">
          
          {/* Freelancer Card */}
          {team && (
            <div className="rounded-2xl border border-white/10 p-6 mb-8" style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                <div className="flex-shrink-0">
                  {team.avatar ? (
                    <img src={team.avatar} alt={team.name} className="w-16 h-16 rounded-xl object-cover" />
                  ) : (
                    <div className="w-16 h-16 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(92, 99, 255, 0.2)' }}>
                      <User className="w-8 h-8" style={{ color: '#5C63FF' }} />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-bold">{team.name}</h3>
                    {team.is_boosted && <Star className="w-4 h-4 text-yellow-500" fill="currentColor" />}
                  </div>
                  <p className="text-sm text-gray-400 flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {team.location?.city || 'Deutschland'}
                  </p>
                </div>
                <div className="px-4 py-2 rounded-lg" style={{ backgroundColor: 'rgba(92, 99, 255, 0.1)' }}>
                  <p className="text-xs text-gray-400">Basispreis</p>
                  <p className="text-lg font-bold" style={{ color: '#5C63FF' }}>€{basePrice}/Min</p>
                </div>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Dein <span style={{ color: '#5C63FF' }}>Projekt</span>
              </h1>
              <p className="text-gray-400 mt-1">Konfiguriere deine Videos individuell</p>
            </div>
            <button
              onClick={addVideo}
              className="flex items-center gap-2 px-5 py-3 rounded-xl font-medium transition-all hover:opacity-90"
              style={{ backgroundColor: '#5C63FF' }}
            >
              <Plus className="w-5 h-5" />
              Video hinzufügen
            </button>
          </div>

          {/* Video Tabs */}
          <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
            {videos.map((video, index) => (
              <button
                key={video.id}
                onClick={() => { setActiveVideoIndex(index); setShowSummary(false); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium whitespace-nowrap transition-all ${
                  activeVideoIndex === index && !showSummary
                    ? 'bg-[#5C63FF] text-white' 
                    : 'bg-white/5 text-gray-400 hover:bg-white/10'
                }`}
              >
                <Video className="w-4 h-4" />
                Video {index + 1}
                {video.videoCount > 1 && (
                  <span className="px-1.5 py-0.5 rounded text-xs bg-white/20">×{video.videoCount}</span>
                )}
              </button>
            ))}
            <button
              onClick={() => setShowSummary(true)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium whitespace-nowrap transition-all ${
                showSummary ? 'bg-[#5C63FF] text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              <Check className="w-4 h-4" />
              Übersicht
            </button>
          </div>

          {/* Video Configuration Panel */}
          {!showSummary && activeVideo && (
            <div className="rounded-2xl border border-white/10 overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
              {/* Video Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center font-bold" style={{ backgroundColor: '#5C63FF' }}>
                    {activeVideoIndex + 1}
                  </div>
                  <div>
                    <h3 className="font-bold">Video {activeVideoIndex + 1}</h3>
                    <p className="text-sm text-gray-400">
                      {activeVideo.videoCount}x • {activeVideo.minutesPerVideo} Min • {pricingConfig?.video_formats?.find(f => f.id === activeVideo.format)?.name}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xl font-bold" style={{ color: '#5C63FF' }}>
                    €{(calculation.videoCalcs?.[activeVideoIndex]?.subtotal || 0).toFixed(0)}
                  </span>
                  <div className="flex gap-1">
                    <button onClick={() => duplicateVideo(activeVideoIndex)} className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white" title="Duplizieren">
                      <Copy className="w-4 h-4" />
                    </button>
                    {videos.length > 1 && (
                      <button onClick={() => removeVideo(activeVideoIndex)} className="p-2 rounded-lg hover:bg-red-500/10 text-gray-400 hover:text-red-400" title="Entfernen">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-8">
                {/* Format */}
                <div>
                  <h4 className="text-sm font-medium text-gray-400 mb-3">Video-Format</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    {availableFormats.map((format) => (
                      <button
                        key={format.id}
                        onClick={() => updateVideo(activeVideoIndex, { format: format.id })}
                        className={`p-4 rounded-xl border transition-all ${
                          activeVideo.format === format.id ? 'border-[#5C63FF] bg-[#5C63FF]/10' : 'border-white/10 hover:border-white/30'
                        }`}
                      >
                        <div className="flex flex-col items-center text-center">
                          {format.id.includes('16_9') && <Monitor className="w-6 h-6 mb-2 text-gray-400" />}
                          {format.id.includes('9_16') && <Smartphone className="w-6 h-6 mb-2 text-gray-400" />}
                          {format.id.includes('1_1') && <Square className="w-6 h-6 mb-2 text-gray-400" />}
                          {format.id.includes('4_5') && <Smartphone className="w-6 h-6 mb-2 text-gray-400" />}
                          {format.id.includes('21_9') && <Film className="w-6 h-6 mb-2 text-gray-400" />}
                          <span className="text-xs font-medium">{format.name}</span>
                          <span className="text-xs text-gray-500">{format.description}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Video Type */}
                <div>
                  <h4 className="text-sm font-medium text-gray-400 mb-3">Video-Typ</h4>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    {pricingConfig?.video_types?.filter(type =>
                      !freelancerPricing?.pricing?.enabled_video_types ||
                      freelancerPricing.pricing.enabled_video_types[type.id] !== false
                    ).map((type) => (
                      <button
                        key={type.id}
                        onClick={() => updateVideo(activeVideoIndex, { videoType: type.id })}
                        className={`p-4 rounded-xl border transition-all text-left ${
                          activeVideo.videoType === type.id ? 'border-[#5C63FF] bg-[#5C63FF]/10' : 'border-white/10 hover:border-white/30'
                        }`}
                      >
                        <p className="font-medium text-sm">{type.name}</p>
                        <p className="text-xs text-gray-500 mt-1">{type.description}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Sprache & Untertitel */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {/* Sprache */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-400 mb-3">Sprache</h4>
                    <select
                      value={activeVideo.language || 'de'}
                      onChange={(e) => updateVideo(activeVideoIndex, { language: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-[#5C63FF] outline-none appearance-none cursor-pointer"
                    >
                      {languages.filter(lang => 
                        !freelancerPricing?.pricing?.enabled_languages || 
                        freelancerPricing.pricing.enabled_languages[lang.id] !== false
                      ).map((lang) => (
                        <option key={lang.id} value={lang.id}>{lang.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Untertitel */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-400 mb-3">
                      Untertitel
                      {activeVideo.subtitleType && activeVideo.subtitleType !== 'none' && (
                        <span className="ml-2 text-[#5C63FF]">
                          (+{subtitleTypes.find(s => s.id === activeVideo.subtitleType)?.price}%)
                        </span>
                      )}
                    </h4>
                    <select
                      value={activeVideo.subtitleType || 'none'}
                      onChange={(e) => updateVideo(activeVideoIndex, { subtitleType: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-[#5C63FF] outline-none appearance-none cursor-pointer"
                    >
                      {subtitleTypes.filter(sub =>
                        sub.id === 'none' ||
                        !freelancerPricing?.pricing?.enabled_subtitle_types ||
                        freelancerPricing.pricing.enabled_subtitle_types[sub.id] !== false
                      ).map((sub) => (
                        <option key={sub.id} value={sub.id}>
                          {sub.name} {sub.price > 0 ? `(+${sub.price}%)` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Untertitel-Farbe (nur wenn Untertitel ausgewählt) */}
                {activeVideo.subtitleType && activeVideo.subtitleType !== 'none' && subtitleTypes.find(s => s.id === activeVideo.subtitleType)?.hasColor && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-400 mb-3">Untertitel-Farbe</h4>
                    <div className="flex gap-3 flex-wrap">
                      {subtitleColors.map((color) => (
                        <button
                          key={color.id}
                          onClick={() => updateVideo(activeVideoIndex, { subtitleColor: color.id })}
                          className={`flex items-center gap-2 px-4 py-2 rounded-xl border transition-all ${
                            activeVideo.subtitleColor === color.id ? 'border-[#5C63FF] bg-[#5C63FF]/10' : 'border-white/10 hover:border-white/30'
                          }`}
                        >
                          <div 
                            className="w-5 h-5 rounded-full border border-white/20" 
                            style={{ backgroundColor: color.id }}
                          />
                          <span className="text-sm">{color.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Video Length & Quantity */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-sm font-medium text-gray-400 mb-3">
                      Video-Länge: <span className="text-white">{activeVideo.minutesPerVideo} Min</span>
                    </h4>
                    <input
                      type="range" min={pricingConfig?.video_length_min ?? 1} max={pricingConfig?.video_length_max ?? 60} value={activeVideo.minutesPerVideo}
                      onChange={(e) => updateVideo(activeVideoIndex, { minutesPerVideo: parseInt(e.target.value) })}
                      className="w-full h-2 rounded-full appearance-none cursor-pointer"
                      style={{ background: `linear-gradient(to right, #5C63FF 0%, #5C63FF ${(activeVideo.minutesPerVideo / (pricingConfig?.video_length_max ?? 60)) * 100}%, rgba(255,255,255,0.1) ${(activeVideo.minutesPerVideo / (pricingConfig?.video_length_max ?? 60)) * 100}%, rgba(255,255,255,0.1) 100%)` }}
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1"><span>{pricingConfig?.video_length_min ?? 1} Min</span><span>{pricingConfig?.video_length_max ?? 60} Min</span></div>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-400 mb-3">
                      Anzahl: <span className="text-white">{activeVideo.videoCount}x</span>
                    </h4>
                    <div className="flex items-center gap-3">
                      <button onClick={() => updateVideo(activeVideoIndex, { videoCount: Math.max(1, activeVideo.videoCount - 1) })} className="w-10 h-10 rounded-xl border border-white/10 hover:border-white/30 flex items-center justify-center text-xl">-</button>
                      <input type="number" min="1" max="50" value={activeVideo.videoCount}
                        onChange={(e) => updateVideo(activeVideoIndex, { videoCount: e.target.value === '' ? '' : Math.max(1, parseInt(e.target.value)) })}
                        className="flex-1 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-center text-lg font-bold focus:border-[#5C63FF] outline-none"
                      />
                      <button onClick={() => updateVideo(activeVideoIndex, { videoCount: Math.min(50, activeVideo.videoCount + 1) })} className="w-10 h-10 rounded-xl border border-white/10 hover:border-white/30 flex items-center justify-center text-xl">+</button>
                    </div>
                  </div>
                </div>

                {/* Raw Material */}
                <div>
                  <h4 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
                    <HardDrive className="w-4 h-4" style={{ color: '#5C63FF' }} />
                    Rohmaterial: <span className="text-white">{formatRawMaterial(activeVideo.rawMaterialMinutes)}</span>
                    {getRawMaterialFactor(activeVideo.rawMaterialMinutes) !== 1 && (
                      <span className={`${getRawMaterialFactor(activeVideo.rawMaterialMinutes) > 1 ? 'text-orange-400' : 'text-green-400'}`}>
                        (×{getRawMaterialFactor(activeVideo.rawMaterialMinutes)})
                      </span>
                    )}
                  </h4>
                  <input
                    type="range" min={pricingConfig?.raw_material_min ?? 0.25} max={pricingConfig?.raw_material_max ?? 1200} step="0.25" value={activeVideo.rawMaterialMinutes}
                    onChange={(e) => updateVideo(activeVideoIndex, { rawMaterialMinutes: parseFloat(e.target.value) })}
                    className="w-full h-2 rounded-full appearance-none cursor-pointer raw-material-slider"
                    style={{ background: 'linear-gradient(to right, #22C55E 0%, #84CC16 25%, #EAB308 50%, #F97316 75%, #DC2626 100%)' }}
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>15 Sek</span>
                    <span
                      data-testid="raw-material-max-label"
                      className="cursor-pointer hover:text-white transition-colors"
                      onDoubleClick={(e) => {
                        const span = e.target;
                        const input = document.createElement('input');
                        input.type = 'number';
                        input.min = '1';
                        input.step = '1';
                        input.value = Math.round(activeVideo.rawMaterialMinutes);
                        input.className = 'w-20 bg-white/10 border border-white/20 rounded px-2 py-0.5 text-xs text-white text-right focus:outline-none focus:border-[#5C63FF]';
                        input.placeholder = 'Min';
                        span.style.display = 'none';
                        span.parentNode.appendChild(input);
                        input.focus();
                        input.select();
                        const finish = () => {
                          const val = parseFloat(input.value);
                          if (val && val > 0) {
                            updateVideo(activeVideoIndex, { rawMaterialMinutes: val });
                          }
                          span.style.display = '';
                          input.remove();
                        };
                        input.addEventListener('blur', finish);
                        input.addEventListener('keydown', (ev) => { if (ev.key === 'Enter') input.blur(); });
                      }}
                      title="Doppelklick zum Bearbeiten"
                    >20 Std</span>
                  </div>
                </div>

                {/* B-Roll & Assets */}
                <div className="p-4 rounded-xl border border-white/10 space-y-4">
                  <label className="flex items-center justify-between cursor-pointer hover:bg-white/5 -m-4 p-4 rounded-xl transition-colors">
                    <div className="flex items-center gap-3">
                      <Clapperboard className="w-5 h-5" style={{ color: '#5C63FF' }} />
                      <div>
                        <p className="font-medium">B-Roll benötigt</p>
                        <p className="text-xs text-gray-500">Zusätzliches Bildmaterial (+15% Basispreis)</p>
                      </div>
                    </div>
                    <div 
                      className={`w-12 h-6 rounded-full transition-colors relative ${activeVideo.needsBRoll ? 'bg-[#5C63FF]' : 'bg-white/10'}`}
                      onClick={() => updateVideo(activeVideoIndex, { needsBRoll: !activeVideo.needsBRoll })}
                    >
                      <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${activeVideo.needsBRoll ? 'translate-x-7' : 'translate-x-1'}`} />
                    </div>
                  </label>
                  
                  {/* B-Roll Zusatzoptionen - nur sichtbar wenn B-Roll aktiviert */}
                  {activeVideo.needsBRoll && pricingConfig?.addon_categories?.find(cat => cat.id === 'broll_assets')?.addons?.length > 0 && (
                    <div className="pt-4 border-t border-white/10 space-y-2">
                      <p className="text-xs text-gray-500 mb-3">B-Roll Zusatzoptionen:</p>
                      {pricingConfig.addon_categories.find(cat => cat.id === 'broll_assets').addons.map((addon) => {
                        const isSelected = activeVideo.addons?.[addon.id];
                        const priceDisplay = addon.flat_price 
                          ? `+€${addon.flat_price}` 
                          : addon.percentage 
                            ? `+${addon.percentage}%` 
                            : '';
                        
                        return (
                          <label 
                            key={addon.id}
                            className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                              isSelected 
                                ? 'bg-[#5C63FF]/20 border border-[#5C63FF]/50' 
                                : 'bg-white/5 border border-transparent hover:border-white/20'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected || false}
                              onChange={() => {
                                updateVideo(activeVideoIndex, {
                                  addons: { ...activeVideo.addons, [addon.id]: !isSelected }
                                });
                              }}
                              className="sr-only"
                            />
                            <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                              isSelected ? 'bg-[#5C63FF] border-[#5C63FF]' : 'border-white/30'
                            }`}>
                              {isSelected && <Check className="w-3 h-3 text-white" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium">{addon.name}</p>
                              {addon.description && (
                                <p className="text-xs text-gray-500 truncate">{addon.description}</p>
                              )}
                            </div>
                            {priceDisplay && (
                              <span className="text-sm font-medium text-[#5C63FF] shrink-0">{priceDisplay}</span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Rush Delivery & Revisionen */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {/* Rush Delivery */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
                      <Clock className="w-4 h-4" style={{ color: '#5C63FF' }} />
                      Liefergeschwindigkeit
                      {activeVideo.rushDelivery && activeVideo.rushDelivery !== 'none' && (
                        <span className="text-[#5C63FF]">
                          (+{rushDeliveryOptions.find(r => r.id === activeVideo.rushDelivery)?.percentage}%)
                        </span>
                      )}
                    </h4>
                    <select
                      value={activeVideo.rushDelivery || 'none'}
                      onChange={(e) => updateVideo(activeVideoIndex, { rushDelivery: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-[#5C63FF] outline-none appearance-none cursor-pointer"
                    >
                      {rushDeliveryOptions.filter(option =>
                        option.id === 'none' ||
                        !freelancerPricing?.pricing?.enabled_rush_options ||
                        freelancerPricing.pricing.enabled_rush_options[option.id] !== false
                      ).map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.name} {option.percentage > 0 ? `(+${option.percentage}%)` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Extra Revisionen */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
                      <ArrowRight className="w-4 h-4" style={{ color: '#5C63FF' }} />
                      Extra Revisionen
                      {activeVideo.extraRevisions && activeVideo.extraRevisions !== 'none' && (
                        <span className="text-[#5C63FF]">
                          (+€{extraRevisionOptions.find(r => r.id === activeVideo.extraRevisions)?.price})
                        </span>
                      )}
                    </h4>
                    <select
                      value={activeVideo.extraRevisions || 'none'}
                      onChange={(e) => updateVideo(activeVideoIndex, { extraRevisions: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-[#5C63FF] outline-none appearance-none cursor-pointer"
                    >
                      {extraRevisionOptions.filter(option =>
                        option.id === 'none' ||
                        !freelancerPricing?.pricing?.enabled_revision_options ||
                        freelancerPricing.pricing.enabled_revision_options[option.id] !== false
                      ).map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.name} {option.price > 0 ? `(+€${option.price})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Addons - Kategorien ohne Rush/Revision (die sind jetzt oben) */}
                {pricingConfig?.addon_categories?.filter(cat => 
                  !separateOptionCategories.includes(cat.id) // Rush Delivery & Revisionen sind jetzt oben
                  // Plattform & Publishing wird angezeigt - nur Freelancer können es nicht einstellen
                ).map((category) => {
                  const availableAddons = getAvailableAddons(category);
                  if (availableAddons.length === 0) return null;
                  const isExpanded = expandedCategories[category.id] !== false;
                  
                  return (
                    <div key={category.id}>
                      <button
                        onClick={() => setExpandedCategories(prev => ({ ...prev, [category.id]: !isExpanded }))}
                        className="w-full flex items-center justify-between mb-3"
                      >
                        <h4 className="text-sm font-medium text-gray-400 flex items-center gap-2">
                          <Layers className="w-4 h-4" style={{ color: '#5C63FF' }} />
                          {category.name}
                        </h4>
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                      </button>
                      {isExpanded && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {availableAddons.map((addon) => {
                            const customPrice = freelancerPricing?.pricing?.custom_prices?.[addon.id];
                            const customPriceType = freelancerPricing?.pricing?.custom_price_types?.[addon.id];
                            const priceType = customPriceType || (addon.percentage !== null ? 'percent' : 'euro');
                            const priceValue = customPrice !== undefined ? customPrice : (addon.percentage || addon.flat_price || 0);
                            
                            return (
                              <button
                                key={addon.id}
                                onClick={() => toggleAddon(addon.id)}
                                className={`p-4 rounded-xl border transition-all text-left ${
                                  activeVideo.addons?.[addon.id] ? 'border-[#5C63FF] bg-[#5C63FF]/10' : 'border-white/10 hover:border-white/30'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-1">
                                    <span className="font-medium text-sm">{addon.name}</span>
                                    {addon.description && <InfoTooltip text={addon.description} />}
                                  </div>
                                  <span className="text-xs text-gray-400">
                                    {priceType === 'percent' ? `+${priceValue}%` : `+€${priceValue}`}
                                  </span>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Notizen pro Video */}
                <div>
                  <h4 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" style={{ color: '#5C63FF' }} />
                    Notizen zu diesem Video
                  </h4>
                  <textarea
                    value={activeVideo.notes || ''}
                    onChange={(e) => updateVideo(activeVideoIndex, { notes: e.target.value })}
                    placeholder="Besondere Wünsche, Hinweise oder Anmerkungen zu diesem Video..."
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-[#5C63FF] outline-none resize-none text-white placeholder-gray-500"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Summary View */}
          {showSummary && (
            <div className="rounded-2xl border border-white/10 overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
              <div className="px-6 py-4 border-b border-white/10">
                <h3 className="font-bold text-lg">Projektübersicht</h3>
                <p className="text-sm text-gray-400">Überprüfe deine Konfiguration vor dem Absenden</p>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Video Details */}
                {videos.map((video, index) => {
                  const videoCalc = calculation.videoCalcs?.[index];
                  const formatName = pricingConfig?.video_formats?.find(f => f.id === video.format)?.name;
                  const typeName = pricingConfig?.video_types?.find(t => t.id === video.videoType)?.name;
                  const langName = languages.find(l => l.id === video.language)?.name;
                  const subtitleName = subtitleTypes.find(s => s.id === video.subtitleType)?.name;
                  const rushName = rushDeliveryOptions.find(r => r.id === video.rushDelivery)?.name;
                  const revisionName = extraRevisionOptions.find(r => r.id === video.extraRevisions)?.name;
                  
                  // Get selected addons
                  const selectedAddons = [];
                  pricingConfig?.addon_categories?.forEach(cat => {
                    cat.addons?.forEach(addon => {
                      if (video.addons?.[addon.id]) {
                        selectedAddons.push(addon.name);
                      }
                    });
                  });
                  
                  return (
                    <div key={video.id} className="rounded-xl border border-white/10 overflow-hidden">
                      {/* Video Header */}
                      <div className="flex items-center justify-between px-4 py-3 bg-white/5">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold" style={{ backgroundColor: '#5C63FF' }}>
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-medium">Video {index + 1}</p>
                            <p className="text-xs text-gray-400">{video.videoCount}x • {video.minutesPerVideo} Min • {formatName}</p>
                          </div>
                        </div>
                        <span className="font-bold text-lg" style={{ color: '#5C63FF' }}>€{(videoCalc?.subtotal || 0).toFixed(0)}</span>
                      </div>
                      
                      {/* Video Details */}
                      <div className="px-4 py-3 space-y-2 text-sm">
                        <div className="grid grid-cols-2 gap-2">
                          <div className="flex justify-between">
                            <span className="text-gray-500">Typ:</span>
                            <span>{typeName}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Sprache:</span>
                            <span>{langName}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Untertitel:</span>
                            <span>{subtitleName}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Rohmaterial:</span>
                            <span>{formatRawMaterial(video.rawMaterialMinutes)}</span>
                          </div>
                          {video.needsBRoll && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">B-Roll:</span>
                              <span className="text-green-400">Ja (+15%)</span>
                            </div>
                          )}
                          {video.rushDelivery !== 'none' && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">Lieferung:</span>
                              <span className="text-orange-400">{rushName}</span>
                            </div>
                          )}
                          {video.extraRevisions !== 'none' && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">Revisionen:</span>
                              <span>{revisionName}</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Selected Addons */}
                        {selectedAddons.length > 0 && (
                          <div className="pt-2 border-t border-white/5">
                            <p className="text-gray-500 text-xs mb-1">Zusatzleistungen:</p>
                            <div className="flex flex-wrap gap-1">
                              {selectedAddons.map((name, i) => (
                                <span key={i} className="px-2 py-0.5 rounded bg-[#5C63FF]/10 text-[#5C63FF] text-xs">{name}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Notes */}
                        {video.notes && (
                          <div className="pt-2 border-t border-white/5">
                            <p className="text-gray-500 text-xs mb-1">Notizen:</p>
                            <p className="text-gray-300 text-xs italic">"{video.notes}"</p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Price Summary */}
                <div className="border-t border-white/10 pt-4 mt-4 space-y-2">
                  <div className="flex justify-between text-gray-400">
                    <span>Zwischensumme ({calculation.totalVideoCount} Videos)</span>
                    <span>€{calculation.nettoTotal.toFixed(0)}</span>
                  </div>
                  <div className="flex justify-between text-gray-400">
                    <span>RevoCut Service ({calculation.marginPercent}%)</span>
                    <span>+€{calculation.marginAmount.toFixed(0)}</span>
                  </div>
                  <div className="flex justify-between text-xl font-bold pt-2 border-t border-white/10">
                    <span>Gesamtpreis</span>
                    <span style={{ color: '#5C63FF' }}>€{calculation.total.toFixed(0)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Bottom Action Bar */}
          <div className="mt-8 flex items-center justify-between">
            <div className="text-sm text-gray-400">
              {calculation.totalVideoCount} Video{calculation.totalVideoCount !== 1 ? 's' : ''} konfiguriert
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm text-gray-400">Gesamtpreis</p>
                <p className="text-2xl font-bold" style={{ color: '#5C63FF' }}>€{calculation.total.toFixed(0)}</p>
              </div>
              <button
                onClick={handleContinue}
                className="flex items-center gap-2 px-8 py-4 rounded-xl font-medium text-lg transition-all hover:opacity-90"
                style={{ backgroundColor: '#5C63FF' }}
              >
                Anfrage senden
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Calculator;
